import { Navigate } from "react-router-dom";
import { useAuth } from "./AuthContext";

/**
 * Wrap any internal route that needs auth. Three render states:
 *   - undefined user → branded interstitial (server is the source of truth)
 *   - null user      → redirect to /login (preserves intended path)
 *   - object user    → render children
 *
 * AuthContext caps the /auth/me call at 5s, so this interstitial is
 * guaranteed to resolve to either a redirect or rendered content within
 * that window — no more "Checking session…" stuck forever.
 */
export default function ProtectedRoute({ children }) {
  const { user } = useAuth();

  if (user === undefined) {
    return (
      <div
        data-testid="auth-loading"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--color-bg-siphon, #100030)",
          fontFamily: "var(--font-family, 'Outfit', sans-serif)",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            aria-hidden="true"
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, #FA98FF, #F050F8 55%, #600E6B)",
              boxShadow:
                "0 0 16px rgba(240,80,248,0.55), inset 0 0 8px rgba(255,255,255,0.15)",
              margin: "0 auto 1rem",
              animation: "procops-pulse 1.4s ease-in-out infinite",
            }}
          />
          <p
            style={{
              color: "var(--color-bg-ice, #F0FFFF)",
              fontSize: "1rem",
              fontWeight: 500,
              margin: "0 0 0.25rem",
              letterSpacing: "-0.01em",
            }}
          >
            ProcOps
          </p>
          <p
            style={{
              color: "var(--color-bg-purple-haze, #A49FC8)",
              fontSize: "0.85rem",
              fontWeight: 300,
              margin: 0,
            }}
          >
            Checking your session…
          </p>
        </div>
        <style>{`
          @keyframes procops-pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50%      { transform: scale(1.15); opacity: 0.75; }
          }
        `}</style>
      </div>
    );
  }

  if (user === null) {
    return <Navigate to="/login" replace />;
  }

  return children;
}
